import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { AuthProvider, useAuth, ROLES } from './context/AuthContext';
import { BotProvider } from './context/BotContext';

// Pages
import Landing        from './pages/Landing';
import Login          from './pages/Login';
import Signup         from './pages/Signup';
import Dashboard      from './pages/Dashboard';
import BotDetail      from './pages/BotDetail';
import AdminDashboard from './pages/Admindashboard';
import WorkerDashboard from './pages/Workerdashboard';

// ── Loading screen ────────────────────────────────────────────
const Loader = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#09090b', color:'#71717a', fontFamily:'Geist, sans-serif', fontSize:'0.875rem' }}>
    Loading...
  </div>
);

// ── Route only for admin role ─────────────────────────────────
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader/>;
  if (!user) return <Navigate to="/login" replace/>;
  if (user.role !== ROLES.ADMIN) return <Navigate to="/dashboard" replace/>;
  return children;
};

// ── Route only for worker role ────────────────────────────────
const WorkerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader/>;
  if (!user) return <Navigate to="/login" replace/>;
  if (user.role !== ROLES.WORKER) return <Navigate to="/dashboard" replace/>;
  return children;
};

// ── Route only for customers (not admin or worker) ────────────
const CustomerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader/>;
  if (!user) return <Navigate to="/login" replace/>;
  // Admins and workers who navigate to /dashboard get redirected
  if (user.role === ROLES.ADMIN)  return <Navigate to="/admin" replace/>;
  if (user.role === ROLES.WORKER) return <Navigate to="/worker" replace/>;
  return children;
};

// ── Public-only route (redirect if already logged in) ─────────
const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader/>;
  if (!user) return children;
  // Redirect based on role after login
  if (user.role === ROLES.ADMIN)  return <Navigate to="/admin" replace/>;
  if (user.role === ROLES.WORKER) return <Navigate to="/worker" replace/>;
  return <Navigate to="/dashboard" replace/>;
};

export default function App() {
  return (
    <AuthProvider>
      <BotProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing/>}/>
            <Route path="/login"  element={<PublicOnly><Login/></PublicOnly>}/>
            <Route path="/signup" element={<PublicOnly><Signup/></PublicOnly>}/>

            {/* Customer dashboard */}
            <Route path="/dashboard"          element={<CustomerRoute><Dashboard/></CustomerRoute>}/>
            <Route path="/dashboard/bot/:botId" element={<CustomerRoute><BotDetail/></CustomerRoute>}/>

            {/* Admin panel — only accessible to admin role */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard/></AdminRoute>}/>

            {/* Worker portal — only accessible to worker role */}
            <Route path="/worker" element={<WorkerRoute><WorkerDashboard/></WorkerRoute>}/>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace/>}/>
          </Routes>
        </BrowserRouter>
      </BotProvider>
    </AuthProvider>
  );
}